import { defineComponent, ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { getHallList } from './api'

/**
 * 对接返回：{ msg, data: { data: [], total, page, page_size, total_pages }, status: 200 }
 * 也兼容 data 直接为数组的旧格式
 */
function extractList(res) {
  if (!res) return []
  const payload = res.data !== undefined ? res.data : res
  if (Array.isArray(payload)) return payload
  if (payload && Array.isArray(payload.data)) return payload.data
  if (payload && Array.isArray(payload.list)) return payload.list
  if (payload && Array.isArray(payload.halls)) return payload.halls
  if (payload && Array.isArray(payload.records)) return payload.records
  if (payload && Array.isArray(payload.items)) return payload.items
  return []
}

function extractMeta(res) {
  const payload = res && res.data !== undefined ? res.data : null
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return {
      total: 0,
      page: undefined,
      page_size: undefined,
      total_pages: 1
    }
  }
  return {
    total: payload.total != null ? Number(payload.total) : 0,
    page: payload.page != null ? Number(payload.page) : undefined,
    page_size:
      payload.page_size != null
        ? Number(payload.page_size)
        : payload.pageSize != null
          ? Number(payload.pageSize)
          : undefined,
    total_pages:
      payload.total_pages != null
        ? Number(payload.total_pages)
        : payload.totalPages != null
          ? Number(payload.totalPages)
          : 1
  }
}

function pickId(row) {
  if (!row || typeof row !== 'object') return '—'
  const v = row.id ?? row.hall_id ?? row.hallId
  return v != null && v !== '' ? String(v) : '—'
}

function pickName(row) {
  if (!row || typeof row !== 'object') return '—'
  const v = row.hallName ?? row.hall_name ?? row.name ?? row.title
  return v != null && String(v).trim() !== '' ? String(v) : '—'
}

function pickHallAlias(row) {
  if (!row || typeof row !== 'object') return '—'
  const v = row.hallAlias ?? row.hall_alias
  return v != null && String(v).trim() !== '' ? String(v) : '—'
}

function pickFirstAncestor(row) {
  if (!row || typeof row !== 'object') return '—'
  const v = row.firstAncestor ?? row.first_ancestor
  return v != null && String(v).trim() !== '' ? String(v) : '—'
}

function pickSurname(row) {
  if (!row || typeof row !== 'object') return '—'
  const v = row.surname ?? row.clan
  return v != null && String(v).trim() !== '' ? String(v) : '—'
}

function pickOriginLocation(row) {
  if (!row || typeof row !== 'object') return '—'
  const v =
    row.originLocation ??
    row.origin_location ??
    row.address ??
    row.location
  return v != null && String(v).trim() !== '' ? String(v) : '—'
}

function pickIntroShort(row) {
  if (!row || typeof row !== 'object') return '—'
  const v = row.intro ?? row.description ?? row.desc
  if (v == null || String(v).trim() === '') return '—'
  const s = String(v)
  return s.length > 48 ? s.slice(0, 48) + '…' : s
}

function pickCoverUrl(row) {
  if (!row || typeof row !== 'object') return ''
  const v = row.coverimg ?? row.coverImg ?? row.cover_url
  return v != null && String(v).trim() !== '' ? String(v) : ''
}

function pickGenealogySummary(row) {
  if (!row || row.genealogy == null || String(row.genealogy).trim() === '') {
    return '无'
  }
  const raw = String(row.genealogy)
  try {
    const j = JSON.parse(raw)
    if (j && j.nodes && typeof j.nodes === 'object') {
      const n = Object.keys(j.nodes).length
      return '有（' + n + ' 节点）'
    }
    return '有'
  } catch (e) {
    return '有（非标准 JSON）'
  }
}

function pickIsCustom(row) {
  if (!row || typeof row !== 'object') return '—'
  const v = row.isCustom ?? row.is_custom
  if (v === 1 || v === true) return '是'
  if (v === 0 || v === false) return '否'
  return '—'
}

function pickPersonalGenealogyId(row) {
  if (!row || typeof row !== 'object') return '—'
  const v = row.personalGenealogyId ?? row.personal_genealogy_id
  return v != null && v !== '' ? String(v) : '—'
}

function pickAddBy(row) {
  if (!row || typeof row !== 'object') return '—'
  const v = row.addBy ?? row.add_by ?? row.add_user_id
  return v != null && v !== '' ? String(v) : '—'
}

export default defineComponent({
  name: 'HallsView',
  setup() {
    const router = useRouter()
    const hallList = ref([])
    const loading = ref(false)
    const errorMsg = ref('')
    const selected = ref(null)

    const currentPage = ref(1)
    const pageSize = ref(20)
    const totalPages = ref(1)
    const total = ref(0)
    const keyword = ref('')

    const buildListParams = () => {
      const params = {
        page: currentPage.value,
        page_size: pageSize.value
      }
      const kw = String(keyword.value || '').trim()
      if (kw) {
        params.keyword = kw
      }
      return params
    }

    const fetchHalls = async () => {
      loading.value = true
      errorMsg.value = ''
      try {
        const res = await getHallList(buildListParams())
        hallList.value = extractList(res)
        const meta = extractMeta(res)
        total.value = meta.total
        totalPages.value = Math.max(1, meta.total_pages || 1)
        if (meta.page != null && !isNaN(meta.page)) {
          currentPage.value = meta.page
        }
        if (meta.page_size != null && !isNaN(meta.page_size)) {
          pageSize.value = meta.page_size
        }

        if (hallList.value.length && selected.value) {
          const sid = pickId(selected.value)
          const still = hallList.value.find(function (r) {
            return pickId(r) === sid
          })
          selected.value = still || null
        } else if (!hallList.value.length) {
          selected.value = null
        }
      } catch (e) {
        console.error(e)
        hallList.value = []
        selected.value = null
        errorMsg.value =
          (e && e.message) || '获取祠堂列表失败，请稍后重试'
      } finally {
        loading.value = false
      }
    }

    const handlePageChange = (page) => {
      currentPage.value = page
      fetchHalls()
    }

    let searchTimer = null
    const handleSearchInput = () => {
      if (searchTimer) {
        clearTimeout(searchTimer)
      }
      searchTimer = setTimeout(() => {
        currentPage.value = 1
        fetchHalls()
      }, 300)
    }

    const handleSearch = () => {
      if (searchTimer) {
        clearTimeout(searchTimer)
        searchTimer = null
      }
      currentPage.value = 1
      fetchHalls()
    }

    const goToVr = (row) => {
      const id = row && row.id != null ? row.id : pickId(row)
      if (id === '—' || id === '') {
        alert('无法跳转：缺少祠堂 ID')
        return
      }
      router.push({ name: 'VrHallEdit', params: { hallId: String(id) } })
    }

    const selectRow = function (row) {
      selected.value = row
    }

    const detailEntries = computed(function () {
      if (!selected.value || typeof selected.value !== 'object') return []
      return Object.keys(selected.value)
        .sort()
        .map(function (k) {
          const val = selected.value[k]
          let display =
            val === null || val === undefined
              ? ''
              : typeof val === 'object'
                ? JSON.stringify(val, null, 2)
                : String(val)
          if (k === 'genealogy' && typeof val === 'string' && val.trim()) {
            try {
              display = JSON.stringify(JSON.parse(val), null, 2)
            } catch (e) {
              /* 保留原始字符串 */
            }
          }
          return { key: k, value: display }
        })
    })

    onMounted(fetchHalls)

    return {
      hallList,
      loading,
      errorMsg,
      selected,
      currentPage,
      pageSize,
      totalPages,
      total,
      keyword,
      pickId,
      pickName,
      pickHallAlias,
      pickFirstAncestor,
      pickSurname,
      pickOriginLocation,
      pickIntroShort,
      pickCoverUrl,
      pickGenealogySummary,
      pickIsCustom,
      pickPersonalGenealogyId,
      pickAddBy,
      selectRow,
      detailEntries,
      fetchHalls,
      handlePageChange,
      handleSearchInput,
      handleSearch,
      goToVr
    }
  }
})
